import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
  resetKey?: string;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render exceptions so a missing API field cannot blank the whole app.
 * Reset when `resetKey` changes (typically the current pathname).
 */
export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('UI render error', error, info.componentStack);
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): React.ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <Box sx={{ p: 3, maxWidth: 560 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          This page failed to load
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Something on this screen was missing or invalid. Reload the page, or go back to the dashboard.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              this.setState({ error: null });
              window.location.assign('/app/dashboard');
            }}
          >
            Go to dashboard
          </Button>
        </Box>
      </Box>
    );
  }
}

/** Error boundary that clears when the route changes. Must render under a Router. */
export const RoutedErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  return <AppErrorBoundary resetKey={pathname}>{children}</AppErrorBoundary>;
};
